import { useCallback, useEffect, useState } from "react";

// The Voxi TTS voice list, read live from the agent server (voxi-2-0 GET /api/voxi-voices).
// That server builds the voices URL from its own VOXI_TTS_URL and keeps the TTS key, so the
// dropdown shows exactly what the server that speaks the call can use. Same v2 base the
// outbound-call service uses.

export interface VoxiTtsVoice {
  id: string;
  name: string;
}

const voxiVoicesUrl = () => {
  const base = (
    import.meta.env.VITE_V2_CALL_SERVICE_URL ||
    import.meta.env.VITE_V1_CALL_SERVICE_URL ||
    ""
  )
    .replace(/\/+$/, "")
    .replace(/\/api\/calls$/, "");
  return `${base}/api/voxi-voices`;
};

// voxi-2-0 answers [{id, name}]; a Cartesia-style `{data}` or `{voices}` is accepted too.
// Entries may be bare names or objects.
const parseVoices = (body: any): VoxiTtsVoice[] => {
  const list = Array.isArray(body) ? body : body?.data ?? body?.voices ?? [];
  return list
    .map((v: any) =>
      typeof v === "string"
        ? { id: v, name: v }
        : { id: v?.id ?? v?.voice_id ?? v?.name, name: v?.name ?? v?.id ?? v?.voice_id },
    )
    .filter((v: VoxiTtsVoice) => typeof v.id === "string" && v.id !== "");
};

export function useVoxiTtsVoices() {
  // null until the server answers; stays null if it fails.
  const [voices, setVoices] = useState<VoxiTtsVoice[] | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    setVoices(null);
    setLoadFailed(false);
    const controller = new AbortController();
    (async () => {
      try {
        const res = await fetch(voxiVoicesUrl(), { signal: controller.signal });
        if (!res.ok) throw new Error(`voices ${res.status}`);
        setVoices(parseVoices(await res.json()));
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error("Failed to load Voxi TTS voices:", error);
        setLoadFailed(true);
      }
    })();
    return () => controller.abort();
  }, [attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { voices, loaded: voices !== null, loadFailed, retry };
}
